import { Stack, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Spacer from '@@components/Spacer';

import { formatShortcutSegments } from '../formatKeys';
import { getShortcutsByCategory, type ShortcutCategory } from '../registry';

const Columns = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${theme.fixed.shortcutsDialog.columnMinWidth}, 1fr))`,
    alignItems: 'start',
    columnGap: theme.spacing(6),
    rowGap: theme.spacing(5),
}));

const Category = styled('section')({
    minWidth: 0,
});

const Row = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    paddingBlock: theme.spacing(1),
    borderBottom: `${theme.borders[1]} solid ${theme.palette.divider}`,
    '&:last-of-type': {
        borderBottom: 'none',
    },
}));

const KeyCombo = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
}));

const Key = styled('kbd')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: theme.spacing(5),
    padding: theme.spacing(1, 2),
    border: `${theme.borders[1]} solid ${theme.palette.divider}`,
    borderBottomWidth: theme.borders[2],
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1,
    whiteSpace: 'nowrap',
}));

const groups = getShortcutsByCategory();

const ShortcutsList = () => {
    const { t } = useTranslation();

    const categoryLabels: Record<ShortcutCategory, string> = {
        page: t('shortcuts.category.page'),
        navigation: t('shortcuts.category.navigation'),
        editor: t('shortcuts.category.editor'),
    };

    return (
        <Columns>
            {groups.map(({ category, shortcuts }) => (
                <Category key={category}>
                    <Typography variant="title4" component="h3">
                        {categoryLabels[category]}
                    </Typography>

                    <Spacer md v />

                    {shortcuts.map(({ id, definition }) => (
                        <Row key={id}>
                            <Typography variant="body2">{t(definition.i18nKey)}</Typography>
                            <KeyCombo>
                                {formatShortcutSegments(id).map((segment, index) => (
                                    <Key key={`${id}-${segment}-${index}`}>{segment}</Key>
                                ))}
                            </KeyCombo>
                        </Row>
                    ))}
                </Category>
            ))}
        </Columns>
    );
};

export default ShortcutsList;
